import type { ActiveCheckoutPointer } from "~/types/payments";

/**
 * sessionStorage pointer that survives a tab refresh mid-payment so
 * `/checkout` and `/payment/[id]` can re-attach to the same PaymentIntent
 * on remount. Pointer lifetime = until the tab closes (sessionStorage
 * semantics) — exactly the right window for a single checkout attempt.
 *
 * Cleared on payment success, on the explicit "Start Again" button, and
 * when the maker switch flow confirms a new order (so a stale pointer
 * doesn't leak into the next checkout).
 *
 * SSR-safe: every storage access is gated on `import.meta.client`.
 */

const ACTIVE_CHECKOUT_KEY = "and-dine-active-checkout";

export function useActiveCheckout() {
  function writePointer(pointer: ActiveCheckoutPointer): void {
    if (!import.meta.client) return;
    sessionStorage.setItem(ACTIVE_CHECKOUT_KEY, JSON.stringify(pointer));
  }

  function readPointer(): ActiveCheckoutPointer | null {
    if (!import.meta.client) return null;
    const raw = sessionStorage.getItem(ACTIVE_CHECKOUT_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as ActiveCheckoutPointer;
    } catch {
      // Corrupt JSON in storage — treat as absent and clear so future
      // reads aren't poisoned.
      sessionStorage.removeItem(ACTIVE_CHECKOUT_KEY);
      return null;
    }
  }

  function clearPointer(): void {
    if (!import.meta.client) return;
    sessionStorage.removeItem(ACTIVE_CHECKOUT_KEY);
  }

  return { writePointer, readPointer, clearPointer, ACTIVE_CHECKOUT_KEY };
}
